import React, { useState, useEffect, useRef } from 'react';
import { AddEscalationLevelModal } from './AddEscalationLevelModal';
import { createEscalationMatrixLevel, deleteEscalationMatrixLevel } from '../../api/escalationMatrix';
import { useEscalationMatrixPage, useEscalationMatrixStore } from '../../state/escalationMatrix';
import { toast } from 'react-toastify';
import { useKeycloak } from '@react-keycloak/web';
import { useParams } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Component for sortable table row
interface SortableTableRowProps {
  level: any;
  index: number;
  onDelete: (levelId: string) => void;
}

const SortableTableRow: React.FC<SortableTableRowProps> = ({ level, index, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: level.id,
    resizeObserverConfig: {}
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`${isDragging ? 'bg-[#f5f5f5]' : 'hover:bg-[#fafafa]'} cursor-move`}
      {...attributes}
    >
      <td className="px-6 py-4 whitespace-nowrap text-sm text-[#171717] border-r border-[#eaeaea]">
        <div className="flex items-center gap-2">
          <div {...listeners} className="cursor-grab active:cursor-grabbing p-1 hover:bg-[#fafafa] rounded">
            <svg className="w-4 h-4 text-[#aaa]" fill="currentColor" viewBox="0 0 20 20">
              <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
            </svg>
          </div>
          <span>{index + 1}</span>
        </div>
      </td>
      <td className="px-6 py-4 text-sm text-[#171717] border-r border-[#eaeaea]">
        {level.description || 'No description'}
      </td>
      <td className="px-6 py-4 text-sm text-[#171717] border-r border-[#eaeaea]">
        {level.name || 'Unnamed level'}
      </td>
      <td className="px-6 py-4 text-sm text-[#171717] border-r border-[#eaeaea]">
        {level.notification_methods && Array.isArray(level.notification_methods) 
          ? level.notification_methods.join(', ') 
          : 'No methods'
        }
      </td>
      <td className="px-6 py-4 text-sm text-[#171717] border-r border-[#eaeaea]">
        {level.escalation_threshold && level.escalation_threshold > 0
          ? `After ${Math.floor(level.escalation_threshold / 60)} hours ${level.escalation_threshold % 60} minutes`
          : 'Immediately'
        }
      </td>
      <td className="px-6 py-4 text-sm text-[#171717]">
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(level.id); }}
          className="text-[#888] hover:text-[#dc2626]"
        >
          Delete
        </button>
      </td>
    </tr>
  );
};

export const EscalationRules: React.FC = () => {
  const [showAddLevelModal, setShowAddLevelModal] = useState(false);
  const { keycloak } = useKeycloak();
  const params = useParams();
  const projectId = params.projectId || '';
  const { loadEscalationMatrixLevels, updateEscalationLevel } = useEscalationMatrixPage(projectId);
  const { levels } = useEscalationMatrixStore();
  const escalationLevels = levels[projectId] || [];
  
  // Local state for sortable levels
  const [sortableLevels, setSortableLevels] = useState<any[]>([]);
  const [isReordering, setIsReordering] = useState(false);

  // Setup sensors for drag & drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Synchronize local state with store data
  useEffect(() => {
    if (isReordering) return; // avoid overwriting optimistic order during reorder
    const filtered = escalationLevels
      .filter(level => level && level.id)
      .sort((a, b) => (a.priority || 0) - (b.priority || 0));
    setSortableLevels(filtered);
  }, [escalationLevels, isReordering]);

      // Load escalation levels on mount. Guard against React StrictMode's
      // dev-only double-invoke firing this twice, while still reloading on a
      // genuine projectId change (this component does not remount when
      // switching projects while already on this tab).
  const lastLoadedProjectIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!projectId || lastLoadedProjectIdRef.current === projectId) return;
    lastLoadedProjectIdRef.current = projectId;
    loadEscalationMatrixLevels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleDeleteLevel = async (levelId: string) => {
    const token = keycloak.token;
    if (!token) return;

    if (!confirm('Are you sure you want to delete this escalation level?')) return;

    try {
      await deleteEscalationMatrixLevel(token, levelId);
      await loadEscalationMatrixLevels();
      toast.success('Escalation level deleted successfully');
    } catch (error: any) {
      console.error('Failed to delete escalation level:', error);
      toast.error(error.message || 'Failed to delete escalation level');
    }
  };

  const handleAddLevel = async (levelData: any) => {
    const token = keycloak.token;
    if (!token || !projectId) {
      throw new Error('Authentication required');
    }

    try {
      await createEscalationMatrixLevel(token, projectId, levelData);
      // Update levels list
      await loadEscalationMatrixLevels();
      toast.success('Escalation level created successfully');
    } catch (error) {
      console.error('Failed to create escalation level:', error);
      throw error;
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      // Save original state for possible rollback
      const originalLevels = [...sortableLevels];
      setIsReordering(true);
      
      const oldIndex = sortableLevels.findIndex((item) => item.id === active.id);
      const newIndex = sortableLevels.findIndex((item) => item.id === over?.id);
      
      const newOrder = arrayMove(sortableLevels, oldIndex, newIndex);
      
      // Update priorities based on new position
      const updatedLevels = newOrder.map((level, index) => ({
        ...level,
        priority: index + 1
      }));

      console.log('New order:', updatedLevels.map(l => ({ id: l.id, name: l.name, priority: l.priority })));
      
      // Update local state immediately for better UX
      setSortableLevels(updatedLevels);

      // Save changes to server
      try {
        // Update priorities sequentially to avoid race conditions
        for (const level of updatedLevels) {
          const originalLevel = originalLevels.find(l => l.id === level.id);
          if (originalLevel && originalLevel.priority !== level.priority) {
            console.log(`Updating ${level.name}: priority ${originalLevel.priority} -> ${level.priority}`);
            await updateEscalationLevel(level.id, { priority: level.priority });
          }
        }

        // Reload from server to ensure persisted order
        await loadEscalationMatrixLevels();

        // After reload, sync local state to server order (sorted by priority)
        const refreshed = (useEscalationMatrixStore.getState().levels[projectId] || [])
          .slice()
          .sort((a, b) => (a.priority || 0) - (b.priority || 0));
        setSortableLevels(refreshed);

        toast.success('Priority order updated successfully!');
      } catch (error) {
        console.error('Failed to save priority changes:', error);
        // Revert to original state on error
        setSortableLevels(originalLevels);
        toast.error('Failed to save priority changes. Please try again.');
      } finally {
        setIsReordering(false);
      }
    }
  };

  return (
    <div className="mt-8">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-medium text-[#171717]">Escalation Rules</h3>
          <p className="text-sm text-[#888] mt-1">
            Drag and drop rows to reorder priority levels
          </p>
        </div>
        <button 
          onClick={() => setShowAddLevelModal(true)}
          className="cursor-pointer bg-white hover:bg-[#f5f5f5] border border-[#f5f5f5] text-[#888] font-bold py-2 px-4 rounded-full"
        >
          + Add Level
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-[#eaeaea] border border-[#eaeaea]">
          <thead className="bg-[#fafafa]">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#888] uppercase tracking-wider border-r border-[#eaeaea]">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-[#aaa]" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
                  </svg>
                  Priority
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#888] uppercase tracking-wider border-r border-[#eaeaea]">
                Description
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#888] uppercase tracking-wider border-r border-[#eaeaea]">
                Level Name
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#888] uppercase tracking-wider border-r border-[#eaeaea]">
                Notification Methods
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#888] uppercase tracking-wider border-r border-[#eaeaea]">
                Escalation Timing
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#888] uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <DndContext 
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <tbody className="bg-white divide-y divide-[#eaeaea]">
              {sortableLevels.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-sm text-[#888]">
                    No escalation levels found. Click "Add Level" to create one.
                  </td>
                </tr>
              ) : (
                <SortableContext 
                  items={sortableLevels.map(level => level.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {sortableLevels.map((level, index) => (
                    <SortableTableRow
                      key={level.id}
                      level={level}
                      index={index}
                      onDelete={handleDeleteLevel}
                    />
                  ))}
                </SortableContext>
              )}
            </tbody>
          </DndContext>
        </table>
      </div>

      <AddEscalationLevelModal
        isOpen={showAddLevelModal}
        onClose={() => setShowAddLevelModal(false)}
        onAddLevel={handleAddLevel}
        projectId={projectId}
      />
    </div>
  );
}; 